import type {
  EventIdRouteParams,
  EventRouteParams,
  GenericIdRouteParams,
  GroupRouteParams,
  SeasonRouteParams,
} from './route-constants';
import { ROUTES } from './route-constants';

/**
 * Type-safe route builder utility class.
 * Provides methods to build route paths programmatically using the centralized constants.
 *
 * All methods return complete route paths that can be used directly with Angular Router.
 *
 * Example usage:
 * ```typescript
 * this.router.navigate([RouteBuilder.events.details('my-event-slug')]);
 * this.router.navigate([RouteBuilder.profile.tickets()]);
 * ```
 */
export class RouteBuilder {
  static root = (): string => `/${ROUTES.FAN_PORTAL.ROOT}`;

  static events = {
    list: (): string => `/${ROUTES.FAN_PORTAL.EVENTS.ROOT}`,
    details: (eventSlug: EventRouteParams['eventSlug']): string =>
      `/${ROUTES.FAN_PORTAL.EVENTS.DETAILS.replace(':eventSlug', eventSlug)}`,
    seatSelection: (eventSlug: EventRouteParams['eventSlug']): string =>
      `/${ROUTES.FAN_PORTAL.EVENTS.SEAT_SELECTION.replace(':eventSlug', eventSlug)}`,
  };

  /**
   * Accepts a number because that is what the API emits (`Season.id`), unlike
   * the slug-based event routes whose parameter is already a string.
   */
  static seasons = {
    details: (seasonId: SeasonRouteParams['seasonId'] | number): string =>
      `/${ROUTES.FAN_PORTAL.SEASONS.DETAILS.replace(':seasonId', String(seasonId))}`,
    seatSelection: (seasonId: SeasonRouteParams['seasonId'] | number): string =>
      `/${ROUTES.FAN_PORTAL.SEASONS.SEAT_SELECTION.replace(':seasonId', String(seasonId))}`,
  };

  /**
   * Keyed on the slug the API publishes, matching the event routes rather than
   * the id-based season ones. A group has no seat selection of its own.
   */
  static groups = {
    details: (groupSlug: GroupRouteParams['groupSlug']): string =>
      `/${ROUTES.FAN_PORTAL.GROUPS.DETAILS.replace(':groupSlug', groupSlug)}`,
  };

  static profile = {
    root: (): string => `/${ROUTES.FAN_PORTAL.PROFILE.ROOT}`,
    user: (): string =>
      `/${ROUTES.FAN_PORTAL.PROFILE.ROOT}/${ROUTES.FAN_PORTAL.PROFILE.USER}`,
    tickets: (): string =>
      `/${ROUTES.FAN_PORTAL.PROFILE.ROOT}/${ROUTES.FAN_PORTAL.PROFILE.TICKETS}`,
    ticketDetails: (eventId: EventIdRouteParams['eventId']): string =>
      `/${ROUTES.FAN_PORTAL.PROFILE.ROOT}/${ROUTES.FAN_PORTAL.PROFILE.TICKETS}/${eventId}`,
  };

  static legal = {
    terms: (): string =>
      `/${ROUTES.FAN_PORTAL.LEGAL.ROOT}/${ROUTES.FAN_PORTAL.LEGAL.TERMS}`,
    privacy: (): string =>
      `/${ROUTES.FAN_PORTAL.LEGAL.ROOT}/${ROUTES.FAN_PORTAL.LEGAL.PRIVACY}`,
  };

  static help = {
    faq: (): string => `/${ROUTES.FAN_PORTAL.HELP.FAQ}`,
    pos: (): string => `/${ROUTES.FAN_PORTAL.HELP.POS}`,
  };

  static controlHubLanding = {
    home: (): string => '/',
    solicitarInformacion: (): string =>
      `/${ROUTES.CONTROL_HUB_LANDING.SOLICITAR_INFORMACION}`,
    sections: ROUTES.CONTROL_HUB_LANDING.SECTIONS,
  };

  static controlHub = {
    dashboard: (): string => `/${ROUTES.CONTROL_HUB.DASHBOARD}`,
    authorization: (): string => `/${ROUTES.CONTROL_HUB.AUTHORIZATION}`,

    boxOffice: {
      root: (): string => `/${ROUTES.CONTROL_HUB.BOX_OFFICE.ROOT}`,
      lastSales: (): string =>
        `/${ROUTES.CONTROL_HUB.BOX_OFFICE.ROOT}/${ROUTES.CONTROL_HUB.BOX_OFFICE.LAST_SALES}`,
      eventDetails: (id: GenericIdRouteParams['id']): string =>
        `/${ROUTES.CONTROL_HUB.BOX_OFFICE.ROOT}/${id}`,
    },

    eventCreation: {
      root: (): string => `/${ROUTES.CONTROL_HUB.EVENT_CREATION.ROOT}`,
      creationSteps: (): string =>
        `/${ROUTES.CONTROL_HUB.EVENT_CREATION.ROOT}/${ROUTES.CONTROL_HUB.EVENT_CREATION.CREATION_STEPS}`,
      creationId: (id: GenericIdRouteParams['id']): string =>
        `/${ROUTES.CONTROL_HUB.EVENT_CREATION.ROOT}/creation/${id}`,
    },

    seatsManagement: {
      root: (): string => `/${ROUTES.CONTROL_HUB.SEATS_MANAGEMENT.ROOT}`,
      eventDetails: (id: GenericIdRouteParams['id']): string =>
        `/${ROUTES.CONTROL_HUB.SEATS_MANAGEMENT.ROOT}/${id}`,
    },

    statistics: (): string => `/${ROUTES.CONTROL_HUB.STATISTICS}`,
    earnings: (): string => `/${ROUTES.CONTROL_HUB.EARNINGS}`,
    reports: (): string => `/${ROUTES.CONTROL_HUB.REPORTS}`,

    settings: {
      root: (): string => `/${ROUTES.CONTROL_HUB.SETTINGS.ROOT}`,
      tickets: (): string =>
        `/${ROUTES.CONTROL_HUB.SETTINGS.ROOT}/${ROUTES.CONTROL_HUB.SETTINGS.TICKETS}`,
      user: (): string =>
        `/${ROUTES.CONTROL_HUB.SETTINGS.ROOT}/${ROUTES.CONTROL_HUB.SETTINGS.USER}`,
    },
  };
}

/**
 * Type utility for compile-time route path extraction.
 *
 * Example usage:
 * ```typescript
 * type EventRoutes = RouteBuilderPaths['events'];
 * // For type-safe route validation in tests or guards
 * ```
 */
export type RouteBuilderPaths = {
  [K in keyof typeof RouteBuilder]: (typeof RouteBuilder)[K] extends (
    ...args: any[]
  ) => string
    ? ReturnType<(typeof RouteBuilder)[K]>
    : RouteBuilderPaths[K];
};
